import UtilityService from '@/services/util/utility-service';
import { ActivityData, ActivityProgressCompletionData } from 'course-types';

/**
 * Service for formatting quiz-related information (dates, attempts, etc.)
 * Centralizes quiz display logic used across activity components.
 */
const QuizFormattingService = {
  /**
   * Get remaining attempts for a quiz
   * Returns -1 for unlimited attempts
   */
  getRemainingAttempts(activity: ActivityData, progress: ActivityProgressCompletionData): number {
    if ((activity.attempts ?? null) == null) return -1;
    const maxAttempts: number = activity.attempts as number;
    const currentAttempts: number = progress.quiz[activity.id]?.attempts ?? 0;
    return maxAttempts - currentAttempts;
  },

  /**
   * Format remaining attempts for display
   */
  formatRemainingAttempts(
    activity: ActivityData,
    progress: ActivityProgressCompletionData,
  ): string {
    if ((activity.attempts ?? null) == null) return 'Unlimited attempts remaining';
    const remaining = this.getRemainingAttempts(activity, progress);
    return `${remaining} attempts remaining`;
  },

  /**
   * Format quiz due date for display
   */
  formatQuizDueDate(date: Date, isLocked?: boolean): string {
    const dueDate = new Date(date);
    const hours = dueDate.getHours() % 12;
    const period = dueDate.getHours() > 11 && dueDate.getHours() < 24 ? 'PM' : 'AM';
    const pastDue = this.isPastDue(dueDate);
    return `${pastDue ? 'Past due' : 'Due by'} ${hours == 0 ? 12 : hours}:${String(dueDate.getMinutes()).padStart(2, '0')} ${period}, ${UtilityService.formatDate(dueDate.toString())}${pastDue && isLocked ? ' (Unavailable)' : ''}`;
  },

  /**
   * Check if a quiz is past due date
   */
  isPastDue(dueDate: Date | null): boolean {
    if (dueDate == null) return false;
    const due = new Date(dueDate);
    const current = new Date(Date.now());
    return current > due;
  },

  /**
   * Determine if a quiz activity should be disabled/clickable
   */
  isQuizDisabled(activity: ActivityData, progress: ActivityProgressCompletionData): boolean {
    const pastDue = this.isPastDue(activity.due_date ?? null);
    const isLocked = (activity.lock_quiz ?? false) && pastDue;
    const noRemainingAttempts = this.getRemainingAttempts(activity, progress) == 0;
    return isLocked || noRemainingAttempts;
  },

  /**
   * Get completion text for any activity type
   */
  getCompletedText(
    type: 'document' | 'video' | 'quiz' | 'resource',
    progress: ActivityProgressCompletionData,
    activityId: string,
  ): string {
    if (type == 'resource') return '';

    if (type == 'quiz') {
      const quizNode = (progress[type] ?? {})[activityId];
      // Display submission date
      if (quizNode && quizNode?.initialSubmissionDate) {
        return `Submitted on ${UtilityService.formatDate(quizNode.initialSubmissionDate.toString())}`;
      }
      // Fall back in case of formatting error
      else if (quizNode && (quizNode?.attempts ?? 0) > 0) {
        return `Submitted`;
      }
    } else {
      // Display completion date
      if ((progress[type] ?? {})[activityId]?.submissionDate) {
        return `${type == 'video' ? 'Viewed' : 'Completed'} on ${new Date((progress[type] ?? {})[activityId].submissionDate as Date).toDateString()}`;
      }
    }
    // Display nothing
    return '';
  },
};

export default QuizFormattingService;
